import { init } from "ys-webrtc-sdk-core";
import { getLinkusSdkSession } from "./telephonyApi";
import { releaseMicrophoneStream } from "./microphoneStreamFactory";
import {
  canRtcSessionAnswer,
  isAckTimeoutCause,
  isAlreadyAnsweredCause,
  isAlreadyAnsweredError,
  isCallerCancelCause,
  isRtcSessionEstablished,
  normalizeLinkusFailureCause,
  toLinkusError,
} from "./linkusSessionUtils";
import {
  getSdkResultMessage,
  isSdkFailureResult,
  isSdkSuccessResult,
  isWebSocketConnectingError,
} from "./linkusSdkResult";

const AUDIO_CALL_OPTIONS = { video: false, offerToReceiveVideo: false };
const REMOTE_AUDIO_RETRY_MS = 500;
const REMOTE_AUDIO_RETRY_LIMIT = 30;
const PHONE_ACTION_MAX_ATTEMPTS = 4;
const REGISTRATION_TIMEOUT_MS = 30000;

/**
 * Linkus Web Core SDK client — same surface as SipClient for useBrowserPhone.
 */
export class LinkusSdkClient {
  constructor(config, handlers = {}) {
    this.config = config;
    this.handlers = handlers;
    this.remoteAudio = handlers.remoteAudio || null;
    this.phone = null;
    this.destroySdk = null;
    this.currentCallId = null;
    this.currentSession = null;
    this.currentDirection = null;
    this.incomingNotifiedCallIds = new Set();
    this.confirmedCallIds = new Set();
    this.remoteAudioWatchTimer = null;
    this.connectPromise = null;
    this.finishingCall = false;
    this.muted = false;
    this.onHold = false;
  }

  updateHandlers(handlers = {}) {
    this.handlers = { ...this.handlers, ...handlers };
    if (handlers.remoteAudio) {
      this.remoteAudio = handlers.remoteAudio;
      this.attachRemoteAudio();
    }
  }

  isReady() {
    return Boolean(this.phone?.isRegistered);
  }

  isOnCall() {
    return Boolean(this.currentCallId);
  }

  async connect() {
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = this.connectInternal();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  async connectInternal() {
    if (!this.config?.enableBrowserPhone) {
      throw new Error("Browser calling is not enabled for D2C Cloud PBX.");
    }

    await this.teardownPhone();

    try {
      const session = await getLinkusSdkSession();
      const operator = await init({
        username: session.username,
        secret: session.loginSignature,
        pbxURL: session.pbxUrl,
      });

      this.phone = operator.phone;
      this.destroySdk = operator.destroy;
      this.bindPhoneEvents();
      this.phone.start();

      await this.waitForTransportReady();
      await this.waitForRegistration();
      if (this.handlers.onRegistered) {
        this.handlers.onRegistered();
      }
    } catch (error) {
      await this.teardownPhone();
      throw error;
    }
  }

  async ensureReady() {
    if (this.connectPromise) {
      await this.connectPromise;
      return;
    }
    await this.ensurePhoneReady();
  }

  async teardownPhone() {
    if (!this.phone && !this.destroySdk) {
      return;
    }
    try {
      if (this.currentCallId && this.phone) {
        this.phone.hangup(this.currentCallId);
      }
      this.phone?.disconnect?.();
      this.phone?.destroy?.();
      this.destroySdk?.();
    } catch {
      // ignore teardown races
    }
    this.phone = null;
    this.destroySdk = null;
    this.currentCallId = null;
    this.currentSession = null;
    this.currentDirection = null;
    this.incomingNotifiedCallIds.clear();
    this.confirmedCallIds.clear();
    this.stopRemoteAudioWatch();
    this.stopRemoteAudio();
    releaseMicrophoneStream();
  }

  bindPhoneEvents() {
    this.phone.on("registered", () => {
      if (this.handlers.onRegistered) {
        this.handlers.onRegistered();
      }
    });

    this.phone.on("registrationFailed", (error) => {
      if (this.handlers.onTransportError) {
        this.handlers.onTransportError(error instanceof Error ? error : new Error(String(error?.message || "REGISTRATION_FAILED")));
      }
    });

    this.phone.on("disconnected", () => {
      if (this.handlers.onTransportError) {
        this.handlers.onTransportError(new Error("Linkus SDK disconnected"));
      }
    });

    this.phone.on("incoming", ({ callId, session }) => {
      this.handleInboundSession(callId, session);
    });

    this.phone.on("startSession", ({ callId, session }) => {
      const direction =
        session?.status?.communicationType === "outbound" ? "OUTBOUND" : "INBOUND";
      if (direction === "INBOUND") {
        this.handleInboundSession(callId, session);
        return;
      }
      this.trackSession(callId, session, direction);
      if (this.handlers.onCallProgress) {
        this.handlers.onCallProgress({
          direction,
          phoneNumber: session?.status?.number,
          callId,
        });
      }
    });

    this.phone.on("deleteSession", ({ callId }) => {
      if (callId !== this.currentCallId) {
        return;
      }
      this.finishCurrentCall();
    });
  }

  handleInboundSession(callId, session) {
    if (this.isOnCall() && this.currentDirection === "OUTBOUND") {
      this.phone?.reject?.(callId);
      return;
    }
    if (this.isOnCall() && this.currentCallId !== callId) {
      this.phone?.reject?.(callId);
      return;
    }
    this.trackSession(callId, session, "INBOUND");
    this.notifyIncomingCall(callId, session);
  }

  waitForTransportReady() {
    return new Promise((resolve, reject) => {
      if (!this.phone) {
        reject(new Error("Linkus SDK is not connected."));
        return;
      }
      if (this.phone.isRegistered) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error("Linkus SDK transport timed out"));
      }, REGISTRATION_TIMEOUT_MS);

      const done = () => {
        cleanup();
        resolve();
      };

      const onFailed = (error) => {
        cleanup();
        reject(error instanceof Error ? error : new Error(String(error?.message || "REGISTRATION_FAILED")));
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.phone?.off?.("connected", done);
        this.phone?.off?.("registered", done);
        this.phone?.off?.("registrationFailed", onFailed);
      };

      this.phone.on("connected", done);
      this.phone.on("registered", done);
      this.phone.on("registrationFailed", onFailed);
    });
  }

  trackSession(callId, session, direction) {
    if (this.currentCallId === callId && this.currentSession === session) {
      return;
    }
    this.currentCallId = callId;
    this.currentSession = session;
    this.currentDirection = direction;
    this.bindSessionEvents(session, direction);
  }

  notifyIncomingCall(callId, session) {
    if (!callId || this.incomingNotifiedCallIds.has(callId)) {
      return;
    }
    this.incomingNotifiedCallIds.add(callId);
    const phoneNumber = session?.status?.number || "Unknown";
    if (this.handlers.onIncomingCall) {
      this.handlers.onIncomingCall({
        id: callId,
        remoteIdentity: { uri: { user: phoneNumber } },
        request: { callId },
      });
    }
  }

  bindSessionEvents(session, direction) {
    if (!session || session.__emmadBound) {
      return;
    }
    session.__emmadBound = true;

    const attach = () => {
      this.attachRemoteAudio();
      this.startRemoteAudioWatch();
    };

    session.on("streamAdded", attach);
    session.on("updateRemoteStream", attach);
    session.on("accepted", attach);
    session.on("progress", attach);

    session.on("peerconnection", (event) => {
      const peerConnection = event?.peerconnection || event;
      if (!peerConnection?.addEventListener) {
        return;
      }
      peerConnection.addEventListener("track", attach);
      peerConnection.addEventListener("connectionstatechange", () => {
        const state = peerConnection.connectionState;
        if (state === "connected" && this.handlers.onMediaConnected) {
          this.handlers.onMediaConnected({ iceState: state });
        }
        if (state === "failed" && this.handlers.onMediaFailure) {
          this.handlers.onMediaFailure({ iceState: state });
        }
        attach();
      });
    });

    session.on("confirmed", () => {
      if (this.currentCallId) {
        this.confirmedCallIds.add(this.currentCallId);
      }
      if (this.handlers.onCallEstablished) {
        this.handlers.onCallEstablished({
          direction,
          phoneNumber: session?.status?.number,
          callId: this.currentCallId,
        });
      }
      attach();
    });

    session.on("failed", (payload) => {
      const causeText = normalizeLinkusFailureCause(payload?.cause ?? payload);
      const established =
        isRtcSessionEstablished(session) ||
        this.confirmedCallIds.has(this.currentCallId) ||
        isAlreadyAnsweredCause(causeText);

      if (established) {
        attach();
        return;
      }

      this.handleSessionFailure(payload, direction);
      this.finishCurrentCall(direction, session?.status?.number, session);
    });

    session.on("ended", () => {
      if (this.handlers.onRemoteBye) {
        this.handlers.onRemoteBye();
      }
      this.finishCurrentCall(direction, session?.status?.number, session);
    });
  }

  handleSessionFailure(payload, direction) {
    const cause = payload?.cause ?? payload;
    const causeText = normalizeLinkusFailureCause(cause);

    if (isAlreadyAnsweredCause(causeText)) {
      return;
    }

    if (isAckTimeoutCause(causeText)) {
      this.handlers.onAckTimeout?.();
      return;
    }

    if (isCallerCancelCause(causeText)) {
      this.handlers.onIncomingCancel?.({ error: cause });
      return;
    }

    if (direction === "INBOUND" && !this.confirmedCallIds.has(this.currentCallId)) {
      if (this.handlers.onIncomingProgressFailed) {
        this.handlers.onIncomingProgressFailed({
          error: toLinkusError(cause, causeText || "INCOMING_FAILED"),
        });
      }
      return;
    }

    if (this.handlers.onMediaFailure) {
      this.handlers.onMediaFailure({ iceState: causeText || "failed" });
    }
  }

  waitForRegistration() {
    return new Promise((resolve, reject) => {
      if (this.phone?.isRegistered) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error("Linkus SDK registration timed out"));
      }, REGISTRATION_TIMEOUT_MS);

      const onRegistered = () => {
        cleanup();
        resolve();
      };

      const onFailed = (error) => {
        cleanup();
        reject(error instanceof Error ? error : new Error(String(error?.message || "REGISTRATION_FAILED")));
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.phone?.off?.("registered", onRegistered);
        this.phone?.off?.("registrationFailed", onFailed);
      };

      this.phone.on("registered", onRegistered);
      this.phone.on("registrationFailed", onFailed);
    });
  }

  finishCurrentCall(direction = this.currentDirection, phoneNumber, session = this.currentSession) {
    if (this.finishingCall || !this.currentCallId) {
      return;
    }
    this.finishingCall = true;
    try {
      const resolvedDirection = direction || this.currentDirection || "OUTBOUND";
      const resolvedNumber = phoneNumber || session?.status?.number;
      if (this.handlers.onCallTerminated) {
        this.handlers.onCallTerminated({
          direction: resolvedDirection,
          phoneNumber: resolvedNumber,
          session,
          callId: this.currentCallId,
        });
      }
      if (this.currentCallId) {
        this.incomingNotifiedCallIds.delete(this.currentCallId);
        this.confirmedCallIds.delete(this.currentCallId);
      }
      this.stopRemoteAudioWatch();
      this.stopRemoteAudio();
      this.currentCallId = null;
      this.currentSession = null;
      this.currentDirection = null;
      this.muted = false;
      this.onHold = false;
    } finally {
      this.finishingCall = false;
    }
  }

  async disconnect() {
    await this.teardownPhone();
  }

  async dial(phoneNumber) {
    if (this.isOnCall()) {
      throw new Error("A call is already in progress. Hang up or tap Reset phone first.");
    }
    const normalized = String(phoneNumber || "").trim();
    if (!normalized) {
      throw new Error("Phone number is required.");
    }
    await this.runPhoneAction(
      () => this.phone.call(normalized, AUDIO_CALL_OPTIONS),
      "Failed to place outbound call"
    );
    this.startRemoteAudioWatch();
  }

  async ensurePhoneReady() {
    if (!this.phone) {
      throw new Error("Linkus SDK is not connected.");
    }
    if (!this.phone.isRegistered) {
      await this.waitForTransportReady();
      await this.waitForRegistration();
    }
  }

  async delay(ms) {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  async runPhoneAction(action, fallbackMessage) {
    let lastError = null;

    for (let attempt = 0; attempt < PHONE_ACTION_MAX_ATTEMPTS; attempt += 1) {
      await this.ensurePhoneReady();
      try {
        const result = await action();
        if (isSdkSuccessResult(result)) {
          return result;
        }
        this.assertSdkSuccess(result, fallbackMessage);
        return result;
      } catch (error) {
        if (isSdkSuccessResult(error)) {
          return error;
        }
        lastError = error;
        const retryable = isWebSocketConnectingError(error);
        if (!retryable || attempt === PHONE_ACTION_MAX_ATTEMPTS - 1) {
          throw error;
        }
        await this.delay(700 * (attempt + 1));
        await this.waitForTransportReady().catch(() => undefined);
        await this.waitForRegistration().catch(() => undefined);
      }
    }

    throw lastError || new Error(fallbackMessage);
  }

  async ensureMicrophonePermission() {
    if (!navigator?.mediaDevices?.getUserMedia) {
      throw new Error("Microphone access is not available in this browser.");
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    stream.getTracks().forEach((track) => track.stop());
  }

  async acceptCall() {
    if (!this.phone || !this.currentCallId) {
      throw new Error("No incoming call to accept.");
    }

    const session = this.currentSession;
    await this.ensureMicrophonePermission();

    if (!isRtcSessionEstablished(session) && canRtcSessionAnswer(session)) {
      try {
        await this.runPhoneAction(
          () => this.phone.answer(this.currentCallId, AUDIO_CALL_OPTIONS),
          "Failed to answer inbound call"
        );
      } catch (error) {
        if (!isAlreadyAnsweredError(error) && !isRtcSessionEstablished(this.currentSession)) {
          throw error;
        }
      }
    }

    this.startRemoteAudioWatch();
    this.attachRemoteAudio();
  }

  assertSdkSuccess(result, fallbackMessage) {
    if (!isSdkFailureResult(result)) {
      return;
    }
    throw new Error(getSdkResultMessage(result, fallbackMessage));
  }

  async rejectCall() {
    if (!this.phone || !this.currentCallId) {
      return;
    }
    this.phone.reject(this.currentCallId);
    this.finishCurrentCall("INBOUND", this.currentSession?.status?.number, this.currentSession);
  }

  async hangup() {
    if (!this.phone || !this.currentCallId) {
      return;
    }
    this.phone.hangup(this.currentCallId);
    this.finishCurrentCall(this.currentDirection, this.currentSession?.status?.number, this.currentSession);
  }

  async forceEndSession() {
    if (this.phone && this.currentCallId) {
      this.phone.hangup(this.currentCallId);
    }
    this.finishCurrentCall();
  }

  setMuted(muted) {
    if (!this.phone || !this.currentCallId) {
      return;
    }
    if (muted && !this.muted) {
      this.phone.mute(this.currentCallId);
      this.muted = true;
    } else if (!muted && this.muted) {
      this.phone.unmute(this.currentCallId);
      this.muted = false;
    }
  }

  async setHold(hold) {
    if (!this.phone || !this.currentCallId) {
      return;
    }
    if (hold && !this.onHold) {
      this.phone.hold(this.currentCallId);
      this.onHold = true;
    } else if (!hold && this.onHold) {
      this.phone.unhold(this.currentCallId);
      this.onHold = false;
    }
  }

  startRemoteAudioWatch() {
    if (this.remoteAudioWatchTimer) {
      return;
    }
    let attempts = 0;
    this.remoteAudioWatchTimer = setInterval(() => {
      attempts += 1;
      this.attachRemoteAudio();
      const hasRemoteAudio = Boolean(this.resolveRemoteStream()?.getAudioTracks?.().length);
      if (hasRemoteAudio || attempts >= REMOTE_AUDIO_RETRY_LIMIT) {
        this.stopRemoteAudioWatch();
      }
    }, REMOTE_AUDIO_RETRY_MS);
  }

  stopRemoteAudioWatch() {
    if (!this.remoteAudioWatchTimer) {
      return;
    }
    clearInterval(this.remoteAudioWatchTimer);
    this.remoteAudioWatchTimer = null;
  }

  resolveRemoteStream() {
    const session = this.currentSession;
    const sdkStream = session?.remoteStream;
    if (sdkStream?.getAudioTracks?.().some((track) => track.readyState === "live")) {
      return sdkStream;
    }
    return this.buildRemoteStreamFromPeerConnection(session);
  }

  buildRemoteStreamFromPeerConnection(session) {
    const peerConnection = session?.RTCSession?.connection;
    if (!peerConnection) {
      return null;
    }
    const remoteStream = new MediaStream();

    peerConnection.getReceivers?.().forEach((receiver) => {
      if (receiver.track?.kind === "audio" && receiver.track.readyState !== "ended") {
        remoteStream.addTrack(receiver.track);
      }
    });

    peerConnection.getTransceivers?.().forEach((transceiver) => {
      const track = transceiver.receiver?.track;
      if (track?.kind === "audio" && track.readyState !== "ended") {
        const alreadyAdded = remoteStream.getTracks().some((existing) => existing.id === track.id);
        if (!alreadyAdded) {
          remoteStream.addTrack(track);
        }
      }
    });

    return remoteStream.getAudioTracks().length > 0 ? remoteStream : null;
  }

  attachRemoteAudio() {
    const audioEl = this.remoteAudio;
    if (!audioEl) {
      return;
    }
    const stream = this.resolveRemoteStream();
    if (!stream) {
      return;
    }
    if (audioEl.srcObject !== stream) {
      audioEl.srcObject = stream;
    }
    audioEl.muted = false;
    audioEl.volume = 1;
    audioEl.play?.().catch((error) => {
      console.warn("Linkus remote audio play failed:", error?.message || error);
    });
  }

  stopRemoteAudio() {
    const audioEl = this.remoteAudio;
    if (!audioEl) {
      return;
    }
    audioEl.pause?.();
    audioEl.srcObject = null;
  }
}
